import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'

const PROJECT_TYPES = [
  { value: 'new_construction', label: 'New Construction' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'cleanup', label: 'Cleanup' }
]

const ROLE_LABELS = {
  sales_bizdev:     'Sales / BizDev',
  primary_contact:  'Primary Contact',
  precon_manager:   'Precon Manager',
  lead_estimator:   'Lead Estimator',
  permitting_lead:  'Permitting Lead',
  procurement_lead: 'Procurement Lead'
}

const NewProjectModal = (props) => {
  const { open, onClose, onSave, url, users } = props
  const [name, setName] = React.useState('')
  const [client, setClient] = React.useState('')
  const [clientId, setClientId] = React.useState(null)
  const [customerOptions, setCustomerOptions] = React.useState([])
  const [customerSearch, setCustomerSearch] = React.useState('')
  const [projectType, setProjectType] = React.useState('')
  const [buildings, setBuildings] = React.useState([])
  const [buildingInput, setBuildingInput] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  // Spec A additions
  const [templates, setTemplates] = React.useState([])
  const [templatesLoading, setTemplatesLoading] = React.useState(false)
  const [templateId, setTemplateId] = React.useState('')
  const [teamMode, setTeamMode] = React.useState(false)
  const [bidDueDate, setBidDueDate] = React.useState('')
  const [projectNotes, setProjectNotes] = React.useState('')
  const [roles, setRoles] = React.useState([])

  // Debounced customer search
  React.useEffect(() => {
    if (customerSearch.length < 2) { setCustomerOptions([]); return }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${url}/api/bid-estimation/customers/search?q=${encodeURIComponent(customerSearch)}`)
        setCustomerOptions(res.data || [])
      } catch (err) { console.error(err) }
    }, 300)
    return () => clearTimeout(timer)
  }, [customerSearch, url])

  // Load templates when modal opens
  React.useEffect(() => {
    if (open) {
      setTemplatesLoading(true)
      axios.get(`${url}/api/bid-estimation/templates`).then(res => {
        setTemplates(res.data || [])
        setTemplatesLoading(false)
      }).catch(err => {
        console.error('Failed to load templates:', err)
        setTemplates([])
        setTemplatesLoading(false)
      })
    }
  }, [open])

  const resetForm = () => {
    setName('')
    setClient('')
    setClientId(null)
    setCustomerOptions([])
    setCustomerSearch('')
    setProjectType('')
    setBuildings([])
    setBuildingInput('')
    setError('')
    setTemplateId('')
    setTeamMode(false)
    setBidDueDate('')
    setProjectNotes('')
    setRoles([])
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleTemplateChange = (e) => {
    const tid = e.target.value
    setTemplateId(tid)
    const template = templates.find(t => t._id === tid)
    if (template) {
      if (template.projectType) setProjectType(template.projectType)
      if (template.defaultRoles) {
        setRoles(template.defaultRoles.map(dr => ({
          role: dr.role,
          userId: '',
          userName: ''
        })))
      }
    }
  }

  const handleTeamToggle = (e) => {
    const isTeam = e.target.checked
    setTeamMode(isTeam)
    if (!isTeam) {
      // Solo: clear role assignments (server auto-assigns)
      setRoles(roles.map(r => ({ ...r, userId: '', userName: '' })))
    }
  }

  const handleRoleChange = (roleKey, userId) => {
    const user = (users || []).find(u => u._id === userId)
    setRoles(roles.map(r =>
      r.role === roleKey
        ? { ...r, userId, userName: user?.name || '' }
        : r
    ))
  }

  const handleAddBuilding = () => {
    const trimmed = buildingInput.trim()
    if (trimmed) {
      setBuildings([...buildings, trimmed])
      setBuildingInput('')
    }
  }

  const handleRemoveBuilding = (index) => {
    setBuildings(buildings.filter((_, i) => i !== index))
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddBuilding()
    }
  }

  const handleSave = () => {
    if (!name.trim()) {
      setError('Project name is required')
      return
    }

    setSaving(true)
    setError('')

    axios.post(`${url}/api/bid-estimation/projects`, {
      name: name.trim(),
      client: client.trim(),
      clientId,
      projectType,
      buildings: buildings.map(b => ({ name: b })),
      templateId: templateId || undefined,
      teamMode,
      bidDueDate: bidDueDate || null,
      projectNotes,
      roles: teamMode ? roles.filter(r => r.userId) : undefined
    }).then((res) => {
      setSaving(false)
      resetForm()
      onSave(res.data)
    }).catch(err => {
      console.error(err)
      setSaving(false)
      setError(err.response?.data?.error || 'Failed to create project')
    })
  }

  const userList = users || []

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">New Project</Typography>
          </Grid>
          <Grid item>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        {/* Template selector */}
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>Template</InputLabel>
          <Select
            value={templateId}
            onChange={handleTemplateChange}
            label="Template"
            disabled={templatesLoading}
          >
            <MenuItem value="">None</MenuItem>
            {templates.map(t => (
              <MenuItem key={t._id} value={t._id}>
                {t.name}{t.isSystemTemplate ? ' (System)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Project Name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
          margin="normal"
          variant="outlined"
          error={!!error && !name.trim()}
          helperText={!name.trim() && error ? error : ''}
        />

        <Autocomplete
          options={customerOptions}
          getOptionLabel={(opt) => opt.displayName || opt.companyName || ''}
          onInputChange={(e, val) => setCustomerSearch(val)}
          onChange={(e, val) => {
            if (val) {
              setClient(val.displayName || val.companyName || '')
              setClientId(val._id)
            } else {
              setClient('')
              setClientId(null)
            }
          }}
          freeSolo
          renderInput={(params) => (
            <TextField {...params} label="Client" fullWidth margin="normal" variant="outlined" />
          )}
        />

        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>Project Type</InputLabel>
          <Select
            value={projectType}
            onChange={e => setProjectType(e.target.value)}
            label="Project Type"
          >
            {PROJECT_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Team mode toggle */}
        <FormControlLabel
          control={<Switch checked={teamMode} onChange={handleTeamToggle} color="primary" />}
          label="Team Mode — assign roles to multiple people"
          style={{ marginTop: 8, marginBottom: 8, display: 'block' }}
        />

        {/* Role assignment - only in team mode */}
        {teamMode && roles.length > 0 && (
          <div style={{ marginTop: 8, marginBottom: 16 }}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Role Assignments</Typography>
            <Grid container spacing={2}>
              {roles.map(r => (
                <Grid item xs={12} sm={6} key={r.role}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>{ROLE_LABELS[r.role] || r.role}</InputLabel>
                    <Select
                      value={r.userId || ''}
                      onChange={e => handleRoleChange(r.role, e.target.value)}
                      label={ROLE_LABELS[r.role] || r.role}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {userList.map(u => (
                        <MenuItem key={u._id} value={u._id}>{u.name || u.email || u._id}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </div>
        )}

        {/* Bid Due Date */}
        <TextField
          label="Bid Due Date"
          type="date"
          value={bidDueDate}
          onChange={e => setBidDueDate(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />

        {/* Project Notes */}
        <TextField
          label="Project Notes"
          value={projectNotes}
          onChange={e => setProjectNotes(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
          multiline
          rows={4}
          rowsMax={6}
          inputProps={{ maxLength: 2000 }}
          helperText={`${projectNotes.length}/2000`}
        />

        {/* Buildings */}
        <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
          Buildings
        </Typography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs>
            <TextField
              label="Building Name"
              value={buildingInput}
              onChange={e => setBuildingInput(e.target.value)}
              onKeyPress={handleKeyPress}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddBuilding}
              disabled={!buildingInput.trim()}
            >
              Add
            </Button>
          </Grid>
        </Grid>

        {buildings.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {buildings.map((building, index) => (
              <Grid
                container
                key={index}
                alignItems="center"
                spacing={1}
                style={{ marginTop: 4 }}
              >
                <Grid item xs>
                  <Typography variant="body2">{building}</Typography>
                </Grid>
                <Grid item>
                  <IconButton size="small" onClick={() => handleRemoveBuilding(index)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </div>
        )}

        {error && name.trim() && (
          <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  users: state.users || []
})

export default connect(mapStateToProps)(NewProjectModal)
